// We are building a Thompson Sampling Bandit.
// That is to say we draw from a Beta(α, β) distribution.
// - α - the number of successful draws to date
// - β - the number of failed draws to date
//
// We use priors of 1, 1.

import { randomBeta } from 'd3-random';
import hash from "../hash";
import { maxBy, logDebug, logError, generateUUID } from '../utils';
import { getPriors } from './priors';

type Options = string[];

type Result = {
  id: string
}

export async function choose(siteId: string, experiment: string, options: Options): Promise<string | null> {
  const experimentIdHash = await hash(experiment);
  const experimentPersistenceKey = `vc_choice_${experimentIdHash}`;
  const existingResult = sessionStorage.getItem(experimentPersistenceKey);

  if (options.length == 0) {
    logError("At least one experimental option is required");
    return null;
  }

  if (existingResult) {
    const result: Result = JSON.parse(existingResult);

    // In case the option no longer exists, we want to rerun the experiment
    const optionExists = options.find((v) => v == result.id);
    if (optionExists !== undefined) {
      return result.id;
    }
  }

  const priors = getPriors(siteId);

  const resultPromises = 
    options.map(async (id) => {
      const variantIdHash = await hash(id);
      const prior = priors[experimentIdHash]?.data[variantIdHash];
      let draw;

      if (prior) {
        const [a, b] = prior;
        draw = randomBeta(a, b)();
      } else {
        draw = randomBeta(1, 1)();
      }

      return {
        id,
        draw,
      };
    })
  
  const results = await Promise.all(resultPromises);
  
  const winner = maxBy(results, (r) => r.draw);
  if (winner) {
    persistResult(experimentPersistenceKey, winner.id);
    return winner.id;
  }

  logDebug("Could not determine an experimental winner");
  return null;
}

function persistResult(experimentPersistenceKey: string, winner: string) {
  const storableResult = {
    id: winner,
  }
  sessionStorage.setItem(experimentPersistenceKey, JSON.stringify(storableResult));
}

export async function recordExposure(siteId: string, experimentId: string, variantId: string) {
  const experimentIdHash = await hash(experimentId);
  const variantIdHash = await hash(variantId);
  const exposurePersistenceKey = `vc_exp_${experimentIdHash}`;

  // We don't need to doubly report the exposure. And we don't need to overcount either
  if (sessionStorage.getItem(exposurePersistenceKey) !== null) return

  const exposure_id = generateUUID();

  const data = {
    experiment_id: experimentIdHash,
    variant_id: variantIdHash,
    exposure_id,
  };

  if (navigator.sendBeacon(`${domain(siteId)}/api/site/${siteId}/exp`, JSON.stringify(data))) {
    logDebug(`Triggered exposure ${variantId} of experiment ${experimentId}`);
    sessionStorage.setItem(exposurePersistenceKey, exposure_id);
  }
}

export function recordSuccess(siteId: string, hashedExperimentId: string) {
  const key = `vc_exp_${hashedExperimentId}`;
  const reportedKey = `${key}_reported`;
  const exposureId = sessionStorage.getItem(key);
  if (exposureId !== null && sessionStorage.getItem(reportedKey) === null) {
    sessionStorage.setItem(reportedKey, "true"); // avoid double-reporting (which wouldn't be tragic, but also entirely unnecessary)
    if (navigator.sendBeacon(`${domain(siteId)}/api/site/${siteId}/${exposureId}/success`)) {
      logDebug(`Triggered success for exposure ${exposureId}`);
    }
  }
}

function domain(siteId: string) {
  let domain = "";
  if (!siteId.startsWith("local_")) {
    domain = "https://version.compare";
  }

  return domain;
}